import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// ----------------------------------------------------------------------------------
// Types aligned with NEW Week 6 revenue forecasting backend (/api/analytics/revenue-forecast)
// ----------------------------------------------------------------------------------

// Raw point from backend service result
interface BackendForecastPoint {
  date: string; // ISO string when serialized
  predicted: number;
  upperBound: number;
  lowerBound: number;
}

// UI-mapped forecast point (keeps prior naming used by chart component)
export interface ForecastDataPoint {
  date: string;
  predictedRevenue: number;
  confidenceLow: number;
  confidenceHigh: number;
}

// Result returned to UI after mapping backend response
export interface ForecastResultUI {
  forecasts: ForecastDataPoint[];
  accuracy: {
    mape: number;
    rmse: number;
    r2: number;
  };
  trend: 'increasing' | 'decreasing' | 'stable';
  seasonality: { detected: boolean; period?: number };
  summary: {
    currentMonthlyRevenue: number;
    predictedNextMonth: number;
    predictedNext3Months: number;
    confidenceLevel: number; // 0-100%
  };
}

export interface HistoricalDataPoint {
  date: string;
  revenue: number;
}

export interface ForecastSummaryNew {
  trend: string;
  currentMonthlyRevenue: number;
  predictedNextMonth: number;
  predictedNext3Months: number;
  confidenceLevel: number;
  accuracy: { mape: number; rmse: number; r2: number };
  seasonality: { detected: boolean; period?: number };
}

export interface GenerateHybridForecastParams {
  forecastDays?: number; // 1-365
  alpha?: number; // 0-1 exponential smoothing parameter
  confidenceLevel?: 90 | 95 | 99; // desired z-score mapping
  saveToDatabase?: boolean; // persist forecast history
}

export interface ForecastHistoryItem {
  id: string;
  forecastDate: string;
  forecastDays: number;
  predictedRevenue: number;
  confidenceLow: number;
  confidenceHigh: number;
  model: string;
  accuracy: number | null;
  mape: number | null;
  rmse: number | null;
  trend: string | null;
  seasonalityDetected: boolean | null;
  seasonalityPeriod: number | null;
  dataPoints: number | null;
  createdAt: string;
}

export interface ForecastHistoryResponse {
  forecasts: ForecastHistoryItem[];
  count: number;
}

export interface AccuracyComparisonModelMetrics {
  model: string;
  avgR2: number;
  avgMAPE: number;
  avgRMSE: number;
  forecastCount: number;
}

export interface AccuracyComparisonResponse {
  models: AccuracyComparisonModelMetrics[];
  bestModel: AccuracyComparisonModelMetrics;
}

// ----------------------------------------------------------------------------------
// Service Client
// ----------------------------------------------------------------------------------
class ForecastingService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  }

  // Generate hybrid forecast (current backend implementation always stores model 'hybrid')
  async generateHybridForecast(params: GenerateHybridForecastParams = {}): Promise<ForecastResultUI> {
    const body = {
      forecastDays: params.forecastDays ?? 30,
      alpha: params.alpha ?? 0.3,
      confidenceLevel: params.confidenceLevel ?? 95,
      saveToDatabase: params.saveToDatabase ?? true,
    };

    const response = await axios.post(
      `${API_BASE_URL}/api/analytics/revenue-forecast/generate`,
      body,
      this.getAuthHeaders()
    );

    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to generate forecast');
    }

    const backend = response.data.data;

    // Map backend forecasts into UI-friendly naming
    const mappedForecasts: ForecastDataPoint[] = (backend.forecasts || []).map((f: BackendForecastPoint) => ({
      date: f.date,
      predictedRevenue: f.predicted,
      confidenceLow: f.lowerBound,
      confidenceHigh: f.upperBound,
    }));

    return {
      forecasts: mappedForecasts,
      accuracy: backend.accuracy,
      trend: backend.trend,
      seasonality: backend.seasonality,
      summary: backend.summary,
    };
  }

  // Dashboard summary (quick stats)
  async getForecastSummary(): Promise<ForecastSummaryNew> {
    const response = await axios.get(
      `${API_BASE_URL}/api/analytics/revenue-forecast/summary`,
      this.getAuthHeaders()
    );
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load forecast summary');
    }
    return response.data.data;
  }

  // Historical forecast objects (persisted runs)
  async getForecastHistory(limit: number = 10, model?: string): Promise<ForecastHistoryResponse> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (model) params.append('model', model);
    const url = `${API_BASE_URL}/api/analytics/revenue-forecast/history?${params.toString()}`;
    const response = await axios.get(url, this.getAuthHeaders());
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load forecast history');
    }
    return response.data.data;
  }

  // Get specific forecast by ID
  async getForecastById(id: string): Promise<any> {
    const response = await axios.get(
      `${API_BASE_URL}/api/analytics/revenue-forecast/${id}`,
      this.getAuthHeaders()
    );
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load forecast');
    }
    return response.data.data;
  }

  // Accuracy comparison across stored models
  async getAccuracyComparison(): Promise<AccuracyComparisonResponse> {
    const response = await axios.get(
      `${API_BASE_URL}/api/analytics/revenue-forecast/accuracy/compare`,
      this.getAuthHeaders()
    );
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load accuracy comparison');
    }
    return response.data.data;
  }

  // Legacy historical revenue (still used for chart historical portion via old endpoint)
  async getHistoricalRevenue(daysBack: number = 90): Promise<HistoricalDataPoint[]> {
    // We reuse the original legacy endpoint until a new one is exposed
    const url = `${API_BASE_URL}/api/forecasting/historical/revenue?daysBack=${daysBack}`;
    const response = await axios.get(url, this.getAuthHeaders());
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load historical revenue');
    }
    return response.data.data;
  }
}

// ----------------------------------------------------------------------------------
// Helper utilities retained (used by existing components)
// ----------------------------------------------------------------------------------
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatPercentage = (value: number): string => `${value.toFixed(2)}%`;

export const getModelLabel = (model: string): string => {
  const labels: Record<string, string> = {
    hybrid: 'Hybrid (Trend + Smoothing)',
    linear: 'Linear Regression', // legacy
    exponential: 'Exponential Smoothing', // legacy
  };
  return labels[model] || model;
};

export const getConfidenceLevelLabel = (level: number): string => `${level}% Confidence`;

// ----------------------------------------------------------------------------------
// Export singleton
// ----------------------------------------------------------------------------------
const forecastingService = new ForecastingService();
export default forecastingService;
